import { ToggleSwitch } from '@primer/react';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useUnifiedShellToggle } from '@upright/ui/views/unified-shell/use-unified-shell';
import { useId } from 'react';
import styled from 'styled-components';

/**
 * The "New interface" switch in the dark header's menus, the desktop user menu and the phone menu
 * (PLT-7968). Off here, it turns the unified shell back on for a user who turned it off; its twin is
 * the switch in the unified shell's account menu. Renders nothing where the user's organisation does
 * not offer that shell, so a caller places it in its own section and styles that through
 * `className`.
 *
 * Primer's switch, as `design/50-components.md` asks until the design system has one, driven by
 * `onClick` rather than `onChange`: the controlled switch also calls `onChange` from an effect on
 * mount, which here would save the choice on every render (see `views/arvo/shared/data-use-switch.tsx`).
 */
export function NewInterfaceSwitch({ className }: { className?: string }) {
  const { available, enabled, setEnabled, pending } = useUnifiedShellToggle();
  const labelId = useId();
  if (!available) {
    return null;
  }
  return (
    <Root className={className}>
      <span id={labelId}>New interface</span>
      <ToggleSwitch
        aria-labelledby={labelId}
        checked={enabled}
        onClick={() => {
          setEnabled(!enabled);
        }}
        loading={pending}
        size="small"
      />
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: ${BouColor.PLATFORM_TEXT_WHITE};
  /* Primer colours its On/Off label for a light ground, and these menus are dark. */
  span[aria-hidden='true'] {
    color: ${BouColor.PLATFORM_TEXT_WHITE};
  }
`;
